import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MapPin, Coins } from "lucide-react";
import { useWallet } from "../hooks/useWallet";
import { AppHeader } from "../components/AppHeader";
import { StatusPill } from "../components/StatusPill";
import { ClaimBadge } from "../components/ClaimSection";
import { AlertsNudge } from "../components/AlertsNudge";
import { NameStep } from "../components/NameStep";
import { OfflineBanner, SyncStatus, InstallCard } from "../components/Pwa";
import { useDispatches } from "../hooks/useDispatches";
import { useProfile } from "../hooks/useProfile";
import { maskPhone, relTime } from "../lib/share";
import { DeleteJobButton } from "../components/DeleteJobButton";
import { PdfButton } from "../components/PdfButton";
import { ExportNudge } from "../components/ExportNudge";
import { isIOS, isStandalone } from "../lib/pwa";
import { SearchBar } from "../components/SearchBar";
import { matchesFilters } from "../lib/jobFilters";
import { speak, summarySpeech } from "../lib/speech";
import { ProofBadge } from "../components/ProofSection";

const FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "pending", label: "Pending", match: (s) => s === "sent" || s === "draft" || s.startsWith("queued") },
  { id: "progress", label: "In progress", match: (s) => s === "viewed" },
  { id: "done", label: "Done", match: (s) => s === "completed" },
];

const pillStatus = (d) => (d.queued && d.sync_state === "failed" ? "failed" : d.status);

export default function JobsList() {
  const { rows, error, fromCache, refresh } = useDispatches();
  const { profile, saveName, welcomeBack, dismissWelcomeBack } = useProfile();
  const { wallet } = useWallet();
  const lowBalance = wallet && wallet.subscription_status !== "active" && wallet.credit_balance <= 2;
  const [filter, setFilter] = useState("all");
  const [ai, setAi] = useState(null);
  const navigate = useNavigate();
  const needsName = profile !== undefined && profile !== null && !profile?.full_name;
  const f = FILTERS.find((x) => x.id === filter);
  const list = (rows || []).filter((d) => f.match(d.status) && matchesFilters(d, ai));
  const onSpoken = (template, filters) => { const hits = (rows || []).filter((d) => matchesFilters(d, filters)); speak(summarySpeech(template, hits), navigator.language); };

  return (
    <main className="shell has-fab" data-testid="jobs-page">
      <AppHeader />
      <OfflineBanner fromCache={fromCache} />
      <SyncStatus />
      {needsName ? <NameStep onSave={saveName} /> : (<>
        <InstallCard />
        {isIOS() && !isStandalone() && localStorage.getItem("dropit-installed") && <p className="text-xs text-muted mb-3 px-1" data-testid="ios-open-app-tip">Tip: open <span className="notranslate" translate="no">DropIt</span> from your Home Screen and sign in with the code to use it as an app.</p>}
        <ExportNudge dispatches={rows} />
        {welcomeBack && (
          <div className="card install-card fade-up border-[rgba(246,201,69,0.5)]" data-testid="welcome-back-banner">
            <div className="flex-1"><p className="font-bold text-[var(--safflower)]">Welcome back</p><p className="text-sm text-muted">Your account was scheduled for deletion due to inactivity — it's been cancelled.</p></div>
            <button type="button" className="icon-btn" onClick={dismissWelcomeBack} aria-label="Dismiss" data-testid="welcome-back-dismiss">×</button>
          </div>
        )}
        {lowBalance && (
          <div className="card install-card fade-up" data-testid="low-balance-card">
            <Coins className="text-[#F6C945]" size={22} />
            <div className="flex-1"><p className="font-bold">Running low — {wallet.credit_balance} credit{wallet.credit_balance === 1 ? "" : "s"} left</p><p className="text-sm text-muted">1 credit = 1 dispatch.</p></div>
            <Link to="/app/billing" className="btn-primary w-auto px-4" data-testid="low-balance-buy">Buy credits</Link>
          </div>
        )}
        <SearchBar filters={ai} onFilters={setAi} onSpoken={onSpoken} />
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4" data-testid="filter-chips">
          {FILTERS.map((x) => (
            <button key={x.id} type="button" className={`filter-chip ${filter === x.id ? "active" : ""}`} onClick={() => setFilter(x.id)} data-testid={`filter-${x.id}`}>{x.label}</button>
          ))}
        </div>
        {error && <p className="text-sm text-[#FF7A7A]" data-testid="jobs-error">{error}</p>}
        {rows && list.length === 0 && (
          <div className="card text-center py-10 fade-up" data-testid="jobs-empty">
            <p className="text-muted">{ai ? "No jobs match your search." : `No ${filter === "all" ? "jobs" : f.label.toLowerCase()} yet.`}</p>
            {filter === "all" && !ai && <button type="button" className="btn-primary mt-5" onClick={() => navigate("/app/new")} data-testid="empty-new-dispatch-button">⚡ New dispatch</button>}
          </div>
        )}
        {ai && rows && <p className="text-xs text-muted mb-2" data-testid="search-result-count">{list.length} match{list.length === 1 ? "" : "es"}</p>}
        <AlertsNudge />
        <div className="stack" data-testid="jobs-list">
          {list.map((d, i) => (
            <Link key={d.id} to={`/app/jobs/${d.id}`} className={`job-card fade-up ${d.status === "canceled" ? "job-canceled" : ""}`} style={{ animationDelay: `${Math.min(i, 6) * 50}ms` }} data-testid={`job-card-${d.id}`} data-status={pillStatus(d)}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-bold leading-tight" data-testid="job-card-title">{d.title || d.task_summary}</h3>
                <div className="flex items-center gap-2"><ClaimBadge dispatch={d} /><ProofBadge dispatch={d} /><StatusPill status={pillStatus(d)} />{d.status === "completed" && <PdfButton dispatch={d} compact />}<DeleteJobButton dispatch={d} compact onDeleted={refresh} /></div>
              </div>
              {d.queued && d.sync_state === "failed" && <p className="text-xs text-[#FF7A7A] mt-1" data-testid="job-card-failed-reason">{d.sync_error === "INSUFFICIENT_CREDITS" ? "Out of credits" : d.sync_error === "WALLET_READ_ONLY" ? "Account read-only" : d.sync_error === "FAIR_USE_LIMIT" ? "Fair-use limit reached" : d.sync_error}</p>}
              <p className="text-sm text-muted mt-2 flex items-center gap-2 flex-wrap">
                {d.recipient_name && <span data-testid="job-card-recipient" className="notranslate" translate="no">→ {d.recipient_name}</span>}
                {!d.recipient_name && d.recipient_phone && <span data-testid="job-card-recipient" className="notranslate" translate="no">{maskPhone(d.recipient_phone)}</span>}
                {!d.recipient_name && !d.recipient_phone && d.recipient_email && <span data-testid="job-card-recipient" className="notranslate" translate="no">{d.recipient_email}</span>}
                <span>·</span><span data-testid="job-card-time">{relTime(d.created_at)}</span>
                {d.properties?.title && <><span>·</span><span className="flex items-center gap-1"><MapPin size={12} />{d.properties.title}</span></>}
              </p>
            </Link>
          ))}
        </div>
        <button type="button" className="fab" onClick={() => navigate("/app/new")} data-testid="new-dispatch-fab"><Plus size={22} strokeWidth={3} /> New dispatch</button>
      </>)}
    </main>
  );
}
